import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import FinalCTA from '../components/FinalCTA';
import Seo from '../components/Seo';
import { Check, Shield, CreditCard, HelpCircle, FileText, ExternalLink } from 'lucide-react';

const CARRIERS = ['Tricare', 'Medicare', 'Anthem (all plans)', 'Sentara', 'Aetna', 'United'];

const PRIVATE_PAY = [
  { tier: 'Interns', price: '$75', note: 'Per individual session' },
  { tier: 'Residents', price: '$100', note: 'Per individual session' },
  { tier: 'Fully Licensed Clinicians', price: '$150', note: 'Per individual session' },
];

export default function Insurance() {
  return (
    <>
      <Seo
        title="Insurance & Billing"
        description="Chesapeake Counseling Center accepts Tricare, Medicare, Anthem, Sentara, Aetna, and United. Private-pay and superbills available for out-of-network reimbursement."
        path="/insurance"
      />
      <PageHero
        eyebrow="Insurance & Billing"
        title="Insurance &"
        highlight="Billing Support"
        subtitle="We understand insurance can be confusing. Our team is here to help you understand your options."
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Insurance' }]}
      />

      {/* Accepted Insurance */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Accepted Insurance</div>
            <h2 className="section-title mb-3">Insurance <span className="text-[color:var(--dusty-rose)]">We Accept</span></h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed">
              We currently accept the following insurance carriers. Please contact our office to confirm coverage for your specific plan.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 stagger">
            {CARRIERS.map((c) => (
              <div key={c} className="card-soft p-5 flex items-center gap-3 reveal">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'rgba(184,190,164,0.22)' }}>
                  <Check className="w-4 h-4 text-[color:var(--sage-dark)]" strokeWidth={2} />
                </div>
                <span className="font-serif-display text-lg text-[color:var(--deep-sage)]">{c}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Private Pay */}
      <section className="section-pad" style={{ background: 'var(--cream)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-center mb-10 reveal">
            <div className="eyebrow mb-3">Private Pay</div>
            <h2 className="section-title mb-3">Private Pay <span className="text-[color:var(--dusty-rose)]">Rates</span></h2>
            <p className="text-[color:var(--taupe-gray)] max-w-2xl mx-auto leading-relaxed">
              We also offer private pay sessions for clients who prefer not to use insurance or need out-of-network flexibility.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 stagger">
            {PRIVATE_PAY.map((p) => (
              <div key={p.tier} className="card-soft p-7 text-center reveal">
                <div className="eyebrow mb-3">{p.tier}</div>
                <div className="font-serif-display text-4xl text-[color:var(--dusty-rose)] mb-2">{p.price}</div>
                <div className="text-sm text-[color:var(--taupe-gray)]">{p.note}</div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-[color:var(--taupe-gray)] italic max-w-2xl mx-auto">
              Couples sessions incur an additional <span className="font-semibold text-[color:var(--deep-sage)]">$50</span> to individual rates as listed above.
            </p>
          </div>
        </div>
      </section>

      {/* Billing Info Cards */}
      <section className="section-pad" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-6">
          {[
            { Icon: Shield, title: 'Verification Support', text: 'Our team can help verify your benefits and answer coverage questions before your first appointment.' },
            { Icon: CreditCard, title: 'Flexible Payment', text: 'Private pay options are available for those who prefer not to use insurance or need out-of-network flexibility.' },
            { Icon: HelpCircle, title: 'Superbills Available', text: 'We can provide superbills for possible reimbursement through out-of-network benefits.' },
            { Icon: FileText, title: 'Good Faith Estimate', text: 'Under the No Surprises Act, private-pay clients receive a Good Faith Estimate outlining expected costs before services begin.' },
          ].map(({ Icon, title, text }) => (
            <div key={title} className="card-soft p-7 reveal">
              <div className="w-12 h-12 rounded-2xl serviceCard-icon flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[color:var(--dusty-rose)]" strokeWidth={1.5} />
              </div>
              <h3 className="font-serif-display text-xl text-[color:var(--deep-sage)] mb-2">{title}</h3>
              <p className="text-sm text-[color:var(--taupe-gray)] leading-relaxed">{text}</p>
            </div>
          ))}
        </div>

        <div className="text-center mt-10">
          <Link to="/contact" className="btn-primary">Ask About Your Insurance</Link>
        </div>
      </section>

      {/* No Surprises Act blurb */}
      <section className="pb-16" style={{ background: 'var(--warm-white)' }}>
        <div className="max-w-4xl mx-auto px-6 md:px-10">
          <div className="card-soft p-8 reveal" style={{ background: 'linear-gradient(135deg, rgba(232,165,169,0.08), rgba(184,190,164,0.12))' }}>
            <div className="eyebrow mb-2">No Surprises Act</div>
            <h3 className="font-serif-display text-2xl text-[color:var(--deep-sage)] mb-3">Your Right to a Good Faith Estimate</h3>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed mb-4">
              Under the federal No Surprises Act, you have the right to receive a Good Faith Estimate explaining how much your medical and mental health care will cost when you are not using insurance. This estimate is provided in advance and helps you plan for the cost of care without hidden surprises.
            </p>
            <p className="text-[color:var(--taupe-gray)] leading-relaxed">
              For more information about the Good Faith Estimate, please visit:{' '}
              <a
                href="https://www.cms.gov/nosurprises"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[color:var(--dusty-rose)] hover:underline font-semibold"
              >
                www.cms.gov/nosurprises <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </p>
          </div>
        </div>
      </section>

      <FinalCTA />
    </>
  );
}
